import { useState } from 'react';
import { useCharacterGearStars, useUpsertGearStar } from '../../lib/characterProgress';
import { GEAR_SLOTS } from '../../lib/gearSlots';
import { useStarforceCatalog } from '../../lib/reference';
import { benchmarkSummary, compareSlotsToBenchmark, type SlotComparison } from '../../lib/starforce';
import { CollapsibleSection } from './CollapsibleSection';

function StarRow({
  label,
  comparison,
  pending,
  onSave,
}: {
  label: string;
  comparison: SlotComparison | undefined;
  pending: boolean;
  onSave: (stars: number) => void;
}) {
  const stars = comparison?.stars ?? 0;
  return (
    <li className="flex min-h-tap items-center gap-3 rounded-lg border border-border bg-bg-elevated-hover px-3 py-2">
      <span className="min-w-0 flex-1 truncate text-sm text-text-muted">{label}</span>
      {comparison?.target ? (
        <span
          className={[
            'rounded px-1.5 py-0.5 text-[0.625rem] font-semibold uppercase tracking-wider',
            comparison.met ? 'bg-accent-dim text-accent' : 'bg-bg-elevated text-text-faint',
          ].join(' ')}
        >
          {comparison.met ? 'Met' : `Target ${comparison.target}★`}
        </span>
      ) : null}
      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          aria-label={`Decrease ${label} stars`}
          disabled={pending || stars <= 0}
          onClick={() => onSave(stars - 1)}
          className="flex min-h-tap min-w-tap items-center justify-center rounded-lg text-text-muted hover:bg-bg-elevated disabled:opacity-30"
        >
          −
        </button>
        <span className="min-w-[2.5rem] text-center text-sm font-semibold tabular-nums text-text">{stars}★</span>
        <button
          type="button"
          aria-label={`Increase ${label} stars`}
          disabled={pending || stars >= 30}
          onClick={() => onSave(stars + 1)}
          className="flex min-h-tap min-w-tap items-center justify-center rounded-lg text-text-muted hover:bg-bg-elevated disabled:opacity-30"
        >
          +
        </button>
      </div>
    </li>
  );
}

export function StarForceSection({ characterId }: { characterId: number }) {
  const starsQuery = useCharacterGearStars(characterId);
  const starforceQuery = useStarforceCatalog();
  const upsert = useUpsertGearStar(characterId);
  const [benchmarkSlug, setBenchmarkSlug] = useState<string>('');

  if (starsQuery.isPending || starforceQuery.isPending) {
    return (
      <CollapsibleSection title="Star Force">
        <p className="text-sm text-text-muted">Loading star force…</p>
      </CollapsibleSection>
    );
  }
  if (starsQuery.isError || starforceQuery.isError) {
    return (
      <CollapsibleSection title="Star Force">
        <p role="alert" className="text-sm text-danger">
          Could not load star force.
        </p>
      </CollapsibleSection>
    );
  }

  const templates = starforceQuery.data?.benchmarkTemplates ?? [];
  const selected = templates.find((t) => t.slug === benchmarkSlug) ?? templates[0] ?? null;
  const slots = starsQuery.data ?? [];
  const comparisons = selected ? compareSlotsToBenchmark(slots, selected) : [];
  const comparisonBySlot = new Map(comparisons.map((c) => [c.slotKey, c]));
  const summary = selected ? benchmarkSummary(comparisons) : null;
  const pendingSlot = upsert.isPending ? upsert.variables?.slotKey : null;

  return (
    <CollapsibleSection title="Star Force" subtitle="Stars per gear slot vs a benchmark target">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-sm text-text-muted">
          Benchmark
          <select
            value={selected?.slug ?? ''}
            onChange={(e) => setBenchmarkSlug(e.target.value)}
            className="min-h-tap rounded-lg border border-border-strong bg-bg-elevated-hover px-3 text-sm text-text focus:border-accent focus:outline-none"
          >
            {templates.map((t) => (
              <option key={t.slug} value={t.slug}>
                {t.name}
              </option>
            ))}
          </select>
        </label>
        {summary && (
          <p className="text-sm font-medium tabular-nums text-text">
            {summary.met}/{summary.total} slots at target
          </p>
        )}
      </div>

      <ul className="grid gap-2 sm:grid-cols-2">
        {GEAR_SLOTS.map((slot) => (
          <StarRow
            key={slot.key}
            label={slot.label}
            comparison={comparisonBySlot.get(slot.key)}
            pending={pendingSlot === slot.key}
            onSave={(stars) => upsert.mutate({ slotKey: slot.key, stars })}
          />
        ))}
      </ul>
    </CollapsibleSection>
  );
}
